import { t } from "../i18n"
// offline_upload_controller.js
// Intercepts file input changes when offline, stores files in IndexedDB,
// and uploads them (via ActiveStorage DirectUpload) once the device is back online.
//
// Usage:
//   data-controller="offline-upload"
//   data-offline-upload-entity-type-value="task|asset"
//   data-offline-upload-entity-id-value="<%= record.id %>"
//   data-offline-upload-attachment-type-value="documents|photos"
//   data-offline-upload-company-id-value="<%= @company.id %>"
//
// Add data-offline-upload-target="queueStatus" element to show queued count.
// Add change->offline-upload#handleFileChange to the file input's data-action.

import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';
import {
  enqueueFile, getPendingFiles, updateFileQueueEntry,
  removeFromFileQueue, getPendingFileCount
} from '../offline_db';

export default class extends Controller {
  static values = {
    entityType:     String,
    entityId:       Number,
    attachmentType: String,
    companyId:      Number
  }
  static targets = ['input', 'queueStatus']

  connect() {
    this._refreshQueueStatus();
    window.addEventListener('online',           this._onOnline  = () => this._drainFileQueue());
    window.addEventListener('taagger:files-drain', this._onDrain = () => this._drainFileQueue());
  }

  disconnect() {
    window.removeEventListener('online',             this._onOnline);
    window.removeEventListener('taagger:files-drain', this._onDrain);
  }

  // ── Handle file selection ─────────────────────────────────────────────────────

  async handleFileChange(event) {
    if (navigator.onLine) return; // Let ActiveStorage/parallel-upload handle it

    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Prevent the form from trying to submit these files
    event.stopImmediatePropagation();

    let queued = 0;
    let totalSize = 0;

    for (const file of files) {
      try {
        const data = await file.arrayBuffer();
        await enqueueFile({
          entity_type:     this.entityTypeValue,
          entity_id:       this.entityIdValue,
          attachment_type: this.attachmentTypeValue,
          company_id:      this.companyIdValue,
          filename:        file.name,
          content_type:    file.type || 'application/octet-stream',
          size:            file.size,
          data
        });
        queued++;
        totalSize += file.size;
      } catch (err) {
        console.warn('[OfflineUpload] Failed to store file:', file.name, err);
      }
    }

    // Clear input so it doesn't interfere with form submission
    event.target.value = '';

    await this._refreshQueueStatus();
    this._dispatch('taagger:file-queued', { count: queued, totalSize });
    this._showToast(t('javascript.controllers.offline_upload_controller.files_queued_toast', { count: queued }), 'warning');
  }

  // ── Drain: upload all pending files for this entity ───────────────────────────

  async _drainFileQueue() {
    if (!navigator.onLine) return;
    if (!this.entityIdValue) return;

    const allPending = await getPendingFiles();
    const myFiles = allPending.filter(f =>
      f.entity_type     === this.entityTypeValue &&
      Number(f.entity_id) === Number(this.entityIdValue) &&
      f.attachment_type === this.attachmentTypeValue
    );

    if (!myFiles.length) return;

    const directUploadUrl = document.querySelector("meta[name='direct-upload-url']")?.content
      || '/rails/active_storage/direct_uploads';

    this._setQueueStatus(t('javascript.controllers.offline_upload_controller.uploading_files', { count: myFiles.length }), 'uploading');

    let uploaded = 0;
    let failed   = 0;

    for (const entry of myFiles) {
      try {
        await updateFileQueueEntry(entry._fqid, { status: 'uploading' });

        // Reconstruct File from stored ArrayBuffer
        const file     = new File([entry.data], entry.filename, { type: entry.content_type });
        const signedId = await this._directUpload(file, directUploadUrl);

        // Attach via PWA endpoint
        const csrfToken = document.querySelector("meta[name='csrf-token']")?.content || '';
        const resp = await fetch(`/pwa/companies/${this.companyIdValue}/attachments`, {
          method:      'POST',
          headers:     {
            'Content-Type': 'application/json',
            'Accept':       'application/json',
            'X-CSRF-Token': csrfToken
          },
          credentials: 'same-origin',
          body:        JSON.stringify({
            entity_type:     entry.entity_type,
            entity_id:       entry.entity_id,
            attachment_type: entry.attachment_type,
            signed_blob_ids: [signedId]
          })
        });

        if (resp.ok) {
          await removeFromFileQueue(entry._fqid);
          uploaded++;
        } else {
          await updateFileQueueEntry(entry._fqid, { status: 'failed', retries: (entry.retries || 0) + 1 });
          failed++;
        }
      } catch (err) {
        await updateFileQueueEntry(entry._fqid, { status: 'failed', retries: (entry.retries || 0) + 1 });
        failed++;
        console.warn('[OfflineUpload] Upload failed for', entry.filename, err);
      }
    }

    await this._refreshQueueStatus();

    if (uploaded > 0) {
      this._showToast(t('javascript.controllers.offline_upload_controller.files_uploaded', { count: uploaded }), 'success');
      // Reload attachments section if possible
      this._dispatch('taagger:attachments-updated', { uploaded });
    }
    if (failed > 0) {
      this._showToast(t('javascript.controllers.offline_upload_controller.files_failed', { count: failed }), 'error');
    }
  }

  // ── DirectUpload wrapper ──────────────────────────────────────────────────────

  _directUpload(file, url) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded / e.total) * 100);
              this._setQueueStatus(t('javascript.controllers.offline_upload_controller.uploading_file_progress', { name: file.name, pct }), 'uploading');
            }
          });
        }
      });
      upload.create((error, blob) => {
        if (error) reject(error);
        else resolve(blob.signed_id);
      });
    });
  }

  // ── UI helpers ────────────────────────────────────────────────────────────────

  async _refreshQueueStatus() {
    const allPending = await getPendingFiles();
    const myPending  = allPending.filter(f =>
      f.entity_type     === this.entityTypeValue &&
      Number(f.entity_id) === Number(this.entityIdValue) &&
      f.attachment_type === this.attachmentTypeValue
    );

    if (myPending.length > 0) {
      const totalSize = myPending.reduce((s, f) => s + (f.size || 0), 0);
      this._setQueueStatus(
        t('javascript.controllers.offline_upload_controller.files_queued_status', { count: myPending.length, size: this._formatSize(totalSize) }),
        'queued'
      );
    } else {
      if (this.hasQueueStatusTarget) {
        this.queueStatusTarget.innerHTML = '';
        this.queueStatusTarget.classList.add('hidden');
      }
    }
  }

  _setQueueStatus(message, type) {
    if (!this.hasQueueStatusTarget) return;
    const styles = {
      queued:    'text-amber-700 bg-amber-50 border-amber-200',
      uploading: 'text-blue-700 bg-blue-50 border-blue-200',
      done:      'text-green-700 bg-green-50 border-green-200',
      error:     'text-red-700 bg-red-50 border-red-200'
    };
    const icons = { queued: '📎', uploading: '⏫', done: '✓', error: '⚠' };
    this.queueStatusTarget.innerHTML = `
      <div class="flex items-center gap-2 text-xs ${styles[type] || styles.queued} border rounded-lg px-3 py-2 mt-2">
        <span>${icons[type] || icons.queued}</span>
        <span>${message}</span>
      </div>`;
    this.queueStatusTarget.classList.remove('hidden');
  }

  _formatSize(bytes) {
    if (bytes < 1024)          return `${bytes} B`;
    if (bytes < 1024 * 1024)   return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  _dispatch(eventName, detail = {}) {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  _showToast(message, type = 'info') {
    const colors = { success: 'bg-green-600', error: 'bg-red-600', warning: 'bg-amber-500', info: 'bg-blue-600' };
    const toast  = document.createElement('div');
    toast.className = `fixed bottom-4 right-4 z-50 ${colors[type] || colors.info} text-white text-sm font-medium px-4 py-3 rounded-xl shadow-lg max-w-sm`;
    toast.style.cssText = 'animation: slideInUp .2s ease;';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 5000);
  }
}
